import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import ShareButton from "@/components/ShareButton";
import { RadonEntryGraphic } from "@/components/RadonEntryGraphic";
import { getStateProgram } from "@/lib/stateRadonPrograms";
import { LEARN_CONTENT } from "@/lib/learnContent";
import { useLearnLang, Rich, LangToggle } from "@/lib/learnLang";
import { Wind, Home, AlertTriangle, ArrowRight, Phone, ExternalLink, HelpCircle } from "lucide-react";

const CARD_ICONS = [Home, Wind, Home, Wind];
const TOC_IDS = ["what-is-radon", "how-it-enters", "why-test"];

const Section = ({ id, eyebrow, title, titleClass = "sm:text-4xl", children }) => (
  <section id={id} className="scroll-mt-24 fade-up" data-testid={`learn-section-${id}`}>
    <p className="eyebrow">{eyebrow}</p>
    <h2 className={`font-serif text-3xl ${titleClass} mt-2 text-[#1C201F]`}>{title}</h2>
    <div className="mt-4 text-[#1C201F] leading-relaxed space-y-4">{children}</div>
  </section>
);

const LevelsTable = ({ t }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="learn-levels-table">
      <thead>
        <tr className="border-b border-[#E8ECEA] text-left text-[10px] tracking-widest uppercase text-[#5C6661]">
          {t.table_head.map((h) => <th key={h} className="py-2 pr-3">{h}</th>)}
        </tr>
      </thead>
      <tbody className="text-[#1C201F]">
        {t.table_rows.map((r, i) => (
          <tr key={r[0]} className={i < t.table_rows.length - 1 ? "border-b border-[#E8ECEA]/60" : ""}>
            <td className="py-2 pr-3 whitespace-nowrap">{r[0]}</td><td className="pr-3">{r[1]}</td><td>{r[2]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StateBlock = ({ prog, t }) => (
  <section className="fade-up delay-2" data-testid="learn-state-block">
    <p className="eyebrow">{t.state_eyebrow}</p>
    <h2 className="font-serif text-3xl mt-2 text-[#1C201F]">{prog.name}</h2>
    <div className="card-flat mt-4">
      {t.state_note && <p className="text-xs text-[#5C6661] mb-2" data-testid="learn-state-note">{t.state_note}</p>}
      <p className="text-[#1C201F] leading-relaxed" lang="en">{prog.blurb}</p>
      <ul className="mt-4 space-y-2 text-sm">
        {prog.phone && (
          <li className="inline-flex items-center gap-2 text-[#1C201F]">
            <Phone size={14} className="text-[#2F524A]" />
            <a className="text-[#2F524A] font-semibold hover:underline" href={`tel:${prog.phone.replace(/[^0-9]/g, "")}`}>
              {prog.phone}
            </a>
          </li>
        )}
        {prog.url && (
          <li>
            <a className="inline-flex items-center gap-1 text-[#2F524A] font-semibold hover:underline" href={prog.url}
              target="_blank" rel="noopener noreferrer" data-testid="learn-state-program-link">
              {prog.programName || t.state_fallback} <ExternalLink size={13} />
            </a>
          </li>
        )}
      </ul>
    </div>
  </section>
);

const Learn = () => {
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [lang, setLang] = useLearnLang();
  const t = LEARN_CONTENT[lang].learn;

  useEffect(() => {
    if (!user) return;
    api.get("/property").then((r) => setProp(r.data)).catch(() => {});
  }, [user]);

  const stateProg = prop?.state ? getStateProgram(prop.state) : null;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 space-y-14" data-testid="learn-page" lang={lang}>
      <header className="fade-up">
        <div className="flex items-center justify-between gap-4">
          <p className="eyebrow">{t.eyebrow}</p>
          <LangToggle lang={lang} onChange={setLang} />
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[#1C201F]" data-testid="learn-title">{t.title}</h1>
        <p className="text-lg text-[#5C6661] mt-4 leading-relaxed max-w-3xl">{t.intro}</p>
        <div className="flex flex-wrap gap-3 mt-6">
          <ShareButton title={t.share_title} text={t.share_text} label={t.share_label} copiedLabel={t.copied} variant="primary" />
          <Link to="/learn/faq" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="learn-faq-link">
            <HelpCircle size={14} /> {t.faq_link}
          </Link>
        </div>
      </header>

      <nav className="card-flat fade-up delay-1" data-testid="learn-toc">
        <p className="eyebrow">{t.toc_title}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
          {t.toc.map((label, i) => (
            <li key={TOC_IDS[i]}>
              <a href={`#${TOC_IDS[i]}`} className="text-[#2F524A] font-semibold hover:underline">{label}</a>
            </li>
          ))}
        </ul>
      </nav>

      <Section id="what-is-radon" eyebrow={t.s1_eyebrow} title={t.s1_title}>
        <p>{t.s1_p1}</p>
        <p className="text-[#5C6661]"><Rich text={t.s1_p2} /></p>
        <div className="card-flat !bg-[#FCF3DC] !border-[#D6A73B] flex items-start gap-3">
          <AlertTriangle size={18} className="text-[#7A5A0F] mt-0.5 shrink-0" />
          <p className="text-sm text-[#7A5A0F] leading-relaxed"><Rich text={t.s1_alert} /></p>
        </div>
      </Section>

      <Section id="how-it-enters" eyebrow={t.s2_eyebrow} title={t.s2_title} titleClass="sm:text-4xl lg:text-[2rem]">
        <p>{t.s2_p1}</p>
        <RadonEntryGraphic lang={lang} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {t.cards.map((c, i) => {
            const Icon = CARD_ICONS[i];
            return (
              <div key={i} className="card-flat">
                <div className="w-9 h-9 rounded-full flex items-center justify-center mb-3" style={{ background: "#F4F7F6", color: "#2F524A" }}>
                  <Icon size={16} />
                </div>
                <p className="font-serif text-lg text-[#1C201F]">{c.title}</p>
                <p className="text-sm text-[#5C6661] mt-1 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
        <p className="text-[#5C6661]"><Rich text={t.s2_p2} /></p>
      </Section>

      <Section id="why-test" eyebrow={t.s3_eyebrow} title={t.s3_title}>
        <p><Rich text={t.s3_p1} /></p>
        <p><Rich text={t.s3_p2} /></p>
        <LevelsTable t={t} />
        <p className="text-[#5C6661]"><Rich text={t.s3_p3} /></p>
      </Section>

      {stateProg && <StateBlock prog={stateProg} t={t} />}

      {!user && (
        <div className="card-flat fade-up delay-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 !p-8">
          <div>
            <p className="eyebrow">{t.cta_eyebrow}</p>
            <h3 className="font-serif text-2xl mt-2">{t.cta_title}</h3>
          </div>
          <Link to="/signup" className="btn-primary inline-flex items-center gap-2" data-testid="learn-cta-signup">
            {t.cta_button} <ArrowRight size={16} />
          </Link>
        </div>
      )}

      {user && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <ShareButton title={t.share_title} text={t.share_text} label={t.share_label} copiedLabel={t.copied} variant="secondary" />
          <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="learn-log-reading">
            {t.log_reading} <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </div>
  );
};

export default Learn;
